// https://stackoverflow.com/questions/49682569/typescript-merge-object-types
type OptionalPropertyNames<T> = {
	[K in keyof T]-?: {} extends {[P in K]: T[K]} ? K : never;
}[keyof T];

type SpreadProperties<L, R, K extends keyof L & keyof R> = {
	[P in K]: L[P] | Exclude<R[P], undefined>;
};

type Id<T> = T extends infer U ? {[K in keyof U]: U[K]} : never;

type SpreadTwo<L, R> = Id<
	Pick<L, Exclude<keyof L, keyof R>> &
		Pick<R, Exclude<keyof R, OptionalPropertyNames<R>>> &
		Pick<R, Exclude<OptionalPropertyNames<R>, keyof L>> &
		SpreadProperties<L, R, OptionalPropertyNames<R> & keyof L>
>;

type Spread<A extends readonly [...any]> = A extends [infer L, ...infer R]
	? SpreadTwo<L, Spread<R>>
	: unknown;

/**
 * Deeply extends an object with another object
 * @param target The object to extend
 * @param source The object to extend with
 * @returns The extended object
 *
 * @example
 * ```ts
 * const obj1 = {a: 1, b: {c: 2}};
 * const obj2 = {b: {d: 3}};
 * const obj3 = deepObjectExtend(obj1, obj2); // {a: 1, b: {c: 2, d: 3}}
 * ```
 */
export const deepObjectExtend = <
	T extends Record<string | number | symbol, any>,
	S extends Record<string | number | symbol, any>,
>(
	target: T,
	source: S,
): Spread<[T, S]> => {
	for (const prop in source) {
		if (source.hasOwnProperty(prop)) {
			if (target[prop] && typeof source[prop] === 'object') {
				deepObjectExtend(target[prop], source[prop]);
			} else {
				target[prop] = source[prop];
			}
		}
	}
	return target as unknown as Spread<[T, S]>;
};
